/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';
import ComplexSpotlightCollectionModuleRenderer from 'bundles/page-config-common/module-renderers/ComplexSpotlightCollectionModuleRenderer';
import SimpleSpotlightCollectionModuleRenderer from 'bundles/page-config-common/module-renderers/SimpleSpotlightCollectionModuleRenderer';
import type { PageConfig_SpotlightCollectionComponentMetadataFragment as SpotlightCollectionComponent } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfig_SpotlightCollectionComponentMetadata';
import type {
  SpotlightCollectionCollectionEntities,
  SpotlightCollectionProductCard,
  SpotlightCollectionRecommendationCollection,
} from 'bundles/page-config-common/types/spotlight-collection';
import { isPathwaysForAllEnabled } from 'bundles/page-config-common/utils/isPathwaysForAllEnabled';

type Props = {
  pageConfigComponent: SpotlightCollectionComponent;
  twoColumnLayout?: boolean;
  hideCoachButton?: boolean;
  showSingleCardDisplay?: boolean;
};

type CollectionElements = {
  recommendationCollection: SpotlightCollectionRecommendationCollection[];
  productCards: SpotlightCollectionProductCard[];
};

function toCollectionElementMapping(collectionElements: SpotlightCollectionCollectionEntities): CollectionElements {
  return collectionElements.reduce(
    (mapping: CollectionElements, element) => {
      if (!element) return mapping;
      if (!element?.__typename) return mapping;
      const __typename = element?.__typename;
      switch (__typename) {
        case 'Recommendation_Collection':
          return {
            ...mapping,
            recommendationCollection: [...mapping.recommendationCollection, { ...element, __typename }],
          };
        case 'ProductCard_ProductCard': {
          return { ...mapping, productCards: [...mapping.productCards, { ...element, __typename }] };
        }
        default: {
          return mapping;
        }
      }
    },
    { recommendationCollection: [], productCards: [] }
  );
}

const SpotlightCollectionModuleRenderer = (props: Props) => {
  const {
    pageConfigComponent: { collectionEntities, secondaryCta },
    twoColumnLayout,
    hideCoachButton,
    showSingleCardDisplay,
  } = props;

  // Impression point for Pathways For All: https://tools.coursera.org/epic/experiment/2_ZY366vEfCAfhJYNo68EQ
  React.useEffect(() => {
    isPathwaysForAllEnabled(true);
  }, []);

  if (!collectionEntities) {
    return null;
  }

  const spotlightCollectionCount = collectionEntities.length;

  return collectionEntities.map((collectionEntity, index) => {
    if (collectionEntity.__typename !== 'Recommendation_Collection') {
      // unexpected type
      return null;
    }

    const enableSingleCardDisplay = showSingleCardDisplay || spotlightCollectionCount > 1;

    const collectionElements = collectionEntity.spotlightCollectionFilters?.elements ?? [];

    const collectionElementMapping = toCollectionElementMapping(collectionElements);

    const isComplexCollection = collectionElementMapping.recommendationCollection.length === collectionElements.length;

    const key = `spotlight-collection-module-${index}`;

    if (isComplexCollection) {
      return (
        <HomeSection className="complex-spotlight-collection-module" key={key}>
          <ComplexSpotlightCollectionModuleRenderer
            showSingleCardDisplay={enableSingleCardDisplay}
            recommendationCollection={{ ...collectionEntity, secondaryCta }}
            recommendationCollections={collectionElementMapping.recommendationCollection}
            twoColumnLayout={twoColumnLayout}
            hideCoachButton={hideCoachButton}
          />
        </HomeSection>
      );
    } else {
      return (
        <HomeSection className="simple-spotlight-collection-module" key={key}>
          <SimpleSpotlightCollectionModuleRenderer
            showSingleCardDisplay={enableSingleCardDisplay}
            recommendationCollection={{ ...collectionEntity, secondaryCta }}
            productCards={collectionElementMapping.productCards}
            twoColumnLayout={twoColumnLayout}
            hideCoachButton={hideCoachButton}
          />
        </HomeSection>
      );
    }
  });
};

export default SpotlightCollectionModuleRenderer;
